import React, { useState, useRef, useEffect } from 'react';
import { auth } from '../../Config/firebase.js';

const StoreList = ({ stores, setStores, selectedStore, setSelectedStore, setItems, setSelectedItem, setShowRemoveModal, setItemToRemove, copyLinkToClipboard }) => {
  const [editingStoreId, setEditingStoreId] = useState(null);
  const [editStoreName, setEditStoreName] = useState('');
  const nameRef = useRef(null);
  const [inputWidth, setInputWidth] = useState('auto');
  const originalStoreNameRef = useRef('');
  const [sharedStoreId, setSharedStoreId] = useState(null);

  const handleStartEdit = (store) => {
    setEditingStoreId(store.id);
    setEditStoreName(store.name);
    originalStoreNameRef.current = store.name;
  };

  useEffect(() => {
    if (editingStoreId && nameRef.current) {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      context.font = 'normal 16px sans-serif';
      const textWidth = context.measureText(editStoreName).width;
      setInputWidth(`${textWidth + 10}px`);
    }
  }, [editingStoreId, editStoreName]);

  const handleSaveEdit = async (storeId) => {
    const trimmedName = editStoreName.trim();
    if (!trimmedName) {
      setEditingStoreId(null);
      return;
    }

    // Find the store being edited
    const storeToUpdate = stores.find(store => store.id === storeId);
    if (!storeToUpdate) {
      setEditingStoreId(null);
      return;
    }

    // Update local state immediately for better UX
    const updatedStores = stores.map((store) =>
      store.id === storeId ? { ...store, name: trimmedName } : store
    );
    setStores(updatedStores);
    if (selectedStore?.id === storeId) {
      setSelectedStore({ ...selectedStore, name: trimmedName });
    }

    try {
      const user = auth.currentUser;
      const idToken = await user.getIdToken(true);

      // Use the new stores/{storeId} endpoint structure (matching StoreData.js)
      const storeDocPath = `projects/xyronis-8cc09/databases/(default)/documents/stores/${storeId}`;
      const storeDocRef = `https://firestore.googleapis.com/v1/${storeDocPath}`;

      const response = await fetch(storeDocRef, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          name: storeDocPath,
          fields: {
            id: { stringValue: storeToUpdate.id },
            name: { stringValue: trimmedName }, // Use the new name
            ownerId: { stringValue: user.uid },
            shareLink: { stringValue: storeToUpdate.shareLink },
            items: {
              arrayValue: {
                values: (storeToUpdate.items || []).map(item => ({
                  mapValue: {
                    fields: {
                      id: { stringValue: item.id },
                      itemName: { stringValue: item.itemName },
                      glbUrl: { stringValue: item.glbUrl },
                      imageUrl: item.imageUrl ? { stringValue: item.imageUrl } : { nullValue: null },
                      timestamp: { stringValue: item.timestamp },
                      category: { stringValue: item.category || 'Furniture' },
                    }
                  }
                }))
              }
            },
            createdAt: { stringValue: storeToUpdate.createdAt || new Date().toISOString() },
            updatedAt: { stringValue: new Date().toISOString() }
          }
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Save failed: ${errorText}`);
      }

      console.log('✅ Store name updated successfully');
    } catch (error) {
      console.error('❌ Save failed:', error.message);
      // Revert local state on error
      setStores(stores);
      if (selectedStore?.id === storeId) {
        setSelectedStore(storeToUpdate);
      }
    }
    setEditingStoreId(null);
  };

  const handleShareClick = (store) => {
    setSharedStoreId(store.id);
    copyLinkToClipboard(store);
    setTimeout(() => setSharedStoreId(null), 2000);
  };

  return (
    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginRight: '20px' }}>
      {stores.map((store) => (
        <div
          key={store.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 20px',
            backgroundColor: selectedStore?.id === store.id ? '#F5F5FF' : '#FCFCFF',
            borderRadius: '10px',
            cursor: 'pointer',
            width: '300px',
          }}
          onClick={() => {
            setSelectedStore(store);
            setItems(store.items || []);
            setSelectedItem(null);
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ fontSize: '20px', color: '#000' }}>≡</div>
            <div>
              {editingStoreId === store.id ? (
                <input
                  type="text"
                  value={editStoreName}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\s+/g, ' ').trimStart();
                    setEditStoreName(value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveEdit(store.id);
                    if (e.key === 'Escape') {
                      setEditStoreName(originalStoreNameRef.current);
                      setEditingStoreId(null);
                    }
                  }}
                  onBlur={() => handleSaveEdit(store.id)}
                  autoFocus
                  ref={nameRef}
                  maxLength="15"
                  style={{
                    fontSize: '16px',
                    color: '#000',
                    fontWeight: 'normal',
                    fontFamily: 'inherit',
                    lineHeight: 'normal',
                    letterSpacing: 'normal',
                    border: 'none',
                    background: 'transparent',
                    outline: 'none',
                    padding: '0',
                    margin: '0',
                    width: inputWidth,
                    appearance: 'none',
                  }}
                />
              ) : (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStartEdit(store);
                  }}
                  style={{ fontSize: '16px', color: '#000', fontWeight: 'normal' }}
                >
                  {store.name}
                </div>
              )}
              <div style={{ fontSize: '14px', color: '#666' }}>{store.items.length} items</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleStartEdit(store);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#000',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                marginLeft: '10px',
                padding: '0',
              }}
            >
              <span className="material-icons" style={{ fontSize: '16px' }}>edit</span>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleShareClick(store);
              }}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '14px',
                color: '#4A90E2',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.3s ease',
              }}
            >
              {sharedStoreId === store.id ? (
                <span
                  className="material-icons"
                  style={{
                    fontSize: '18px',
                    color: '#000',
                    textDecoration: 'none',
                    opacity: 1,
                    animation: 'fadeInCheck 0.3s ease-in-out',
                  }}
                >
                  check
                </span>
              ) : (
                <span
                  style={{
                    textDecoration: 'underline',
                    animation: sharedStoreId === store.id ? 'fadeInShare 0.3s ease-in-out' : 'none',
                  }}
                >
                  Share
                </span>
              )}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowRemoveModal(true);
                setItemToRemove({ id: store.id, name: store.name });
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#000',
                cursor: 'pointer',
                fontSize: '16px',
                padding: '0',
              }}
            >
              ✕
            </button>
          </div>
        </div>
      ))}
      <style>
        {`
          @keyframes fadeInCheck {
            0% { opacity: 0; transform: scale(0.8); }
            100% { opacity: 1; transform: scale(1); }
          }
          @keyframes fadeInShare {
            0% { opacity: 0; transform: scale(0.8); }
            100% { opacity: 1; transform: scale(1); }
          }
        `}
      </style>
    </div>
  );
};

export default StoreList;